import { useMemo, useState } from 'react';
import { GridView } from '@vivli/shared/views';
import {
    ResearchTeamMemberAccessStatusEnum,
    ResearchTeamMemberAccountStatusEnum,
    ResearchTeamMemberRoleEnum,
    VmAdminApprovalStatusEnum
} from '@vivli/features/data-requests/infrastructure/enum';
import { IDropdownMenuItem } from '@vivli/shared/infrastructure/interface';
import { IDataRequest, IResearchTeamMemberDetail, IResearchTeamMemberStatus } from '@vivli/features/data-requests/infrastructure/interface';
import { EllipsisDropdownMenuComponent } from '@vivli/shared/components';
import { ColDef, IHeaderParams } from 'ag-grid-community';
import { AdminApprovalConstant } from '@vivli/shared/infrastructure/constants';
import { GridCellRendererEnum, PermissionsEnum } from '@vivli/shared/infrastructure/enum';
import { useResearchTeamCreate, useResearchTeamPermissions } from '@vivli/features/data-requests/infrastructure/hook';
import { finalize, first } from 'rxjs/operators';
import { IUserEmailParam } from '@vivli/features/users/infrastructure/interface';
import { useDataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { TeamMemberModalComponent } from './team-member-modal.component';
import { DraftTeamMemberModalComponent } from './draft-team-member-modal.component';
import { useFormContext, useFormState } from 'react-hook-form';
import {
    useActiveUserContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useUsersApiContext
} from '@vivli/core/infrastructure/context';

// DEV-516: derive the "Status Details" display strings from the backend computed status Dto.
// Behavior-preserving with the former persisted StatusArray, including the no-account special case.
const buildStatusLabels = (status?: IResearchTeamMemberStatus): string[] => {
    if (!status) {
        // No computed status yet (new member pre-first-save): show the create-time defaults
        // (No Account, both access states Pending) using the same label maps as the computed branch.
        return [
            AdminApprovalConstant.accountStatusLabels[ResearchTeamMemberAccountStatusEnum.NoAccount],
            AdminApprovalConstant.accessToDataRequestLabels[ResearchTeamMemberAccessStatusEnum.Pending],
            AdminApprovalConstant.accessToDataLabels[ResearchTeamMemberAccessStatusEnum.Pending],
            'Data Access Training Not Completed'
        ];
    }

    return [
        AdminApprovalConstant.accountStatusLabels[status.account],
        AdminApprovalConstant.accessToDataRequestLabels[status.accessToDataRequest],
        AdminApprovalConstant.accessToDataLabels[status.accessToData],
        status.trainingCompleted ? 'Data Access Training Completed' : 'Data Access Training Not Completed'
    ];
};

// DEV-747: data access requires an established Vivli account — the member's userId is what authorizes
// them for the data, so there is nothing to grant until the account exists. A row with no computed status
// yet counts as No Account, matching buildStatusLabels' defaults above.
const hasVivliAccount = (teamMember: IResearchTeamMemberDetail): boolean =>
    !!teamMember.statusDto && teamMember.statusDto.account !== ResearchTeamMemberAccountStatusEnum.NoAccount;

interface ResearchTeamGridComponentProps {
    members: IResearchTeamMemberDetail[];
    draftMode: boolean;
    itemHasError?: boolean;
    onRemove?: (teamMember: IResearchTeamMemberDetail) => void;
    onGrantDuaApproval?: (teamMember: IResearchTeamMemberDetail) => void;
    onRevokeDuaApproval?: (teamMember: IResearchTeamMemberDetail) => void;
    onRevokeVmAccess?: (teamMember: IResearchTeamMemberDetail) => void;
    onMakeOwner?: (teamMember: IResearchTeamMemberDetail) => void;
    onSetAccessTraining?: (teamMember: IResearchTeamMemberDetail) => void;
    onChange?: (dataRequest: IDataRequest) => void;
}

export const ResearchTeamGridComponent = ({
    members,
    draftMode,
    onRemove,
    itemHasError,
    onGrantDuaApproval,
    onRevokeDuaApproval,
    onRevokeVmAccess,
    onMakeOwner,
    onChange
}: ResearchTeamGridComponentProps) => {
    const { dataRequest, isAdminEditing, isActiveUserOrgAdmin, handleFormSavePublish } = useDataRequestContext();
    // const [menuItems, setMenuItems] = useState<IDropdownMenuItem[]>();
    // const [subTitle, setSubTitle] = useState<string>(null);
    // const [userAccountStatus, setUserAccountStatus] = useState<string>();
    // const [isDaTrained, setUserDaTrained] = useState<string>();
    const { userCanEdit, userCanManage, userCanDelete, canTransferAdminRole } = useResearchTeamPermissions(dataRequest, isAdminEditing);
    const formApi = useFormContext();
    // DEV-519: subscribe to the whole-DR form's dirty state so a 3-dots action can persist unsaved edits first.
    const { isDirty } = useFormState({ control: formApi.control });
    const arrayName = 'researchTeam.researchTeamDetails';
    const modalService = useModalApiContext();
    const { user, hasPermission } = useActiveUserContext();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);
    const createResearcher = useResearchTeamCreate();
    const getMemberArrayPath = (index: number) => `${arrayName}.${index}`;
    const dataRequestsService = useDataRequestsApiContext();
    const [isLoading, setIsLoading] = useState(false);
    const usersService = useUsersApiContext();

    // DEV-458: the REST layer rethrows the response body as a string, so reading `.message` off it was
    // undefined and produced a blank popup. Show the string directly (clean quotes/newlines), mirroring
    // the whole-DR save's handleError in data-request-context.wrapper.
    const handleError = (error) => {
        const cleanError = typeof error === 'string' ? error.replace(/\r|\n|"/g, ' ').trim() : error?.message;
        modalService.error(cleanError);
    };

    // DEV-519: push the server-returned DR up to the parent for an in-place grid update (replaces the old
    // full-page reload). The parent routes it through the data-request context (updateDataRequest).
    const handleChange = (dataRequest: IDataRequest) => onChange?.(dataRequest);

    const handleCleanup = () => {
        setIsLoading(false);
    };

    // DEV-519: if the whole-DR form has unsaved edits, persist them before running the per-member action,
    // so the follow-up context update (which resets the form) doesn't discard them. No-op when clean.
    const runAfterSavingIfDirty = (action: () => void) => {
        if (!isDirty) {
            action();
            return;
        }
        handleFormSavePublish(false)
            .then(() => action())
            .catch(() => {
                // Save failed; handleFormSavePublish already surfaced the error. Don't act on unsaved state.
            });
    };

    const setApprovalStatus = (isApprove: boolean, teamMember: IResearchTeamMemberDetail) => {
        const approvalVerb = isApprove ? 'grant' : 'deny';
        const confirmMsg = `Are you sure you want to ${approvalVerb} access to the data for ${teamMember.firstName} ${teamMember.lastName}?`;
        modalService.confirm(confirmMsg, {
            title: 'Set Data Access Approval',
            onConfirm: () => {
                runAfterSavingIfDirty(() => {
                    setIsLoading(true);
                    dataRequestsService
                        .setTeamMemberAdminApproval(dataRequest.id, teamMember, isApprove ? 'true' : 'false')
                        .pipe(first(), finalize(handleCleanup))
                        .subscribe((dr) => handleChange(dr), handleError);
                });

                // // Don't even THINK about using teamMember.statusArray in the forEach loop
                // let tempWorkArray = teamMember.statusArray;
                //
                // let itemFound = false;
                // let n = 0;
                // tempWorkArray.forEach((value) => {
                //     if ((value === 'Access to Data Denied' || value === 'Access to Data Pending') && isApprove) {
                //         tempWorkArray[n] = 'Access to Data Granted';
                //         itemFound = true;
                //         return teamMember;
                //     }
                //     if ((value === 'Access to Data Granted' || value === 'Access to Data Pending') && !isApprove) {
                //         tempWorkArray[n] = 'Access to Data Denied';
                //         itemFound = true;
                //         return teamMember;
                //     }
                //     n = n + 1;
                // });
                // if (!itemFound) {
                //     if (isApprove) {
                //         tempWorkArray?.push('Access to Data Granted');
                //     }
                //     if (!isApprove) {
                //         tempWorkArray?.push('Access to Data Denied');
                //     }
                // }
                // if (isApprove) {
                //     teamMember.adminApprovalStatus = VmAdminApprovalStatusEnum.Approved;
                // } else {
                //     teamMember.adminApprovalStatus = VmAdminApprovalStatusEnum.Disapproved;
                // }
                //
                // teamMember.statusArray = tempWorkArray;
                // getMenuItems(teamMember);
            }
        });
        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnRemove = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to remove "${teamMember.firstName} ${teamMember.lastName}"?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                onRemove(teamMember);
            }
        });
    };

    const handleRevokeAccessRequest = (requestMessage: string, dataRequest: IDataRequest, teamMember: IResearchTeamMemberDetail) => {
        modalService.confirm(requestMessage, {
            onConfirm: () => {
                runAfterSavingIfDirty(() => {
                    setIsLoading(true);
                    dataRequestsService
                        .revokeVmAccess(dataRequest.id, teamMember.teamMemberId)
                        .pipe(first(), finalize(handleCleanup))
                        .subscribe((dr) => handleChange(dr), handleError);
                });

                // let itemFound = false;
                //
                // const tempWorkArray = teamMember.statusArray.map((status) => {
                //     if (status === 'Access to Data Request Granted' || status === 'Access to Data Request Pending') {
                //         itemFound = true;
                //         return 'Access to Data Request Denied';
                //     }
                //
                //     if (status === 'Access to Data Request Granted for Admin') {
                //         itemFound = true;
                //         return status;
                //     }
                //
                //     return status;
                // });
                //
                // if (!itemFound) {
                //     tempWorkArray?.push('Access to Data Request Denied');
                // }
                //
                // teamMember.hasAccessToVirtualMachineResource = false;
                // getMenuItems(teamMember);
                // teamMember.statusArray = tempWorkArray;
            }
        });

        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    const handleOnRevokeVmAccess = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to revoke access for "${teamMember.firstName} ${teamMember.lastName}"?`;
        handleRevokeAccessRequest(confirmMsg, dataRequest, teamMember);
    };

    const handleOnGrantDuaApproval = (teamMember) => {
        setApprovalStatus(true, teamMember);
        teamMember.adminApprovalStatus = 'Approved';
        getMenuItems(teamMember);
    };

    const handleOnRevokeDuaApproval = (teamMember) => {
        setApprovalStatus(false, teamMember);
        teamMember.adminApprovalStatus = 'Disapproved';
        getMenuItems(teamMember);
    };

    const handleOnMakeOwner = (teamMember: IResearchTeamMemberDetail) => {
        const confirmMsg = `Are you sure you want to make "${teamMember.firstName} ${teamMember.lastName}" the Admin of the data request?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                runAfterSavingIfDirty(() => {
                    setIsLoading(true);
                    dataRequestsService
                        .changeDataRequestOwner(dataRequest.id, teamMember)
                        .pipe(first(), finalize(handleCleanup))
                        .subscribe((dr) => handleChange(dr), handleError);
                });
                // let itemFound = false;
                // const tempRolesArray = teamMember.rolesArray.map((role: string) => {
                //     if (role === 'Admin') {
                //         itemFound = true;
                //     }
                //     return role;
                // });
                //
                // if (!itemFound) {
                //     tempRolesArray.push('Admin');
                // }
                // teamMember.rolesArray = tempRolesArray;
            }
        });

        // DEV-519: removed the optimistic formApi.setValue that dirtied the whole-DR form on menu action;
        // the BE now returns the recomputed RolesDto and handleChange updates the grid row in place.
    };

    const handleOnSetUnSetAccessTraining = (isDaTrainingComplete, teamMember) => {
        onSetUnSetAccessTraining(isDaTrainingComplete, teamMember);
    };

    const onSetUnSetAccessTraining = (isDaTrainingComplete: boolean, teamMember: IResearchTeamMemberDetail) => {
        runAfterSavingIfDirty(() => {
            setIsLoading(true);
            usersService
                .setUserDaTraining(teamMember.userId, isDaTrainingComplete)
                .pipe(first(), finalize(handleCleanup))
                .subscribe(() => {
                    // DEV-519: training lives on the user, so setUserDaTraining returns a status string, not a DR.
                    // Re-fetch the DR to pick up the recomputed training status, then update the grid row in place.
                    dataRequestsService
                        .getDataRequest(dataRequest.id)
                        .pipe(first())
                        .subscribe((dr) => handleChange(dr), handleError);
                }, handleError);
        });
    };

    const handleOnGrantRequestAccessQuery = async (teamMember) => {
        const canGrant = await checkAccountResolvable(teamMember);

        if (!canGrant) {
            return;
        }

        const confirmMsg = `Are you sure you want to grant "${teamMember.firstName} ${teamMember.lastName}" access to data request?`;
        modalService.confirm(confirmMsg, {
            onConfirm: () => {
                runAfterSavingIfDirty(() => {
                    setIsLoading(true);
                    // DEV-510: direct grant of access to the data request (no invitation record).
                    dataRequestsService
                        .grantAccessToDataRequest(dataRequest.id, teamMember.teamMemberId)
                        .pipe(first(), finalize(handleCleanup))
                        .subscribe(
                            (dr) => {
                                handleChange(dr);
                            },
                            (error) => {
                                modalService.error(error);
                            }
                        );
                });

                // let itemFound = false;
                // const tempWorkArray = teamMember.statusArray.map((status) => {
                //     if (status === 'Access to Data Request Denied' || status === 'Access to Data Request Pending') {
                //         itemFound = true;
                //         return 'Access to Data Request Granted';
                //     }
                //
                //     return status;
                // });
                //
                // if (!itemFound) {
                //     tempWorkArray?.push('Access to Data Request Granted');
                // }
                // teamMember.hasAccessToVirtualMachineResource = true;
                // getMenuItems(teamMember);
                // teamMember.statusArray = tempWorkArray;
            }
        });

        // const updatedTeamMembers = dataRequest.researchTeam.researchTeamDetails.map((tm) => {
        //     if (tm.id === teamMember.id) {
        //         return teamMember;
        //     }
        //     return tm;
        // });
        //
        // formApi.setValue(arrayName, updatedTeamMembers, { shouldDirty: true, shouldValidate: true });
    };

    // Confirms the team member's email resolves before granting. Granting to disabled / no-account
    // users is intentionally allowed (DEV-569); other validations are handled by the back end.
    const checkAccountResolvable = (teamMember): Promise<boolean> => {
        const emailParam: IUserEmailParam = { emailAddress: teamMember.email };

        return usersService
            .getUserByEmail(emailParam)
            .pipe(first())
            .toPromise()
            .then(() => true)
            .catch(() => {
                modalService.error(`Error fetching user: ${teamMember.email}`);
                return false;
            });
    };

    const getMenuItems = (teamMember) => {
        const resultMenuItems: IDropdownMenuItem[] = [];

        if (onRemove && !teamMember.isLeadResearcher && !teamMember.isStatistician && !teamMember.isAdmin && userCanDelete) {
            resultMenuItems.push({
                title: 'Remove Team Member',
                onClick: () => handleOnRemove(teamMember),
                dataIdPrefix: 'remove-team-member'
            });
        }

        // DEV-747 / RQ-134: not offered while the member has No Account — see hasVivliAccount.
        if (teamMember.accessToData !== ResearchTeamMemberAccessStatusEnum.Granted && isVivliAdmin && hasVivliAccount(teamMember)) {
            resultMenuItems.push({
                title: 'Grant Access to data',
                onClick: () => handleOnGrantDuaApproval(teamMember),
                dataIdPrefix: 'grant-dua-approval'
            });
        }

        //TODO PERMFE - redo with permissions+
        if (teamMember.accessToData !== ResearchTeamMemberAccessStatusEnum.Denied && isVivliAdmin) {
            resultMenuItems.push({
                title: 'Deny Access to data',
                onClick: () => handleOnRevokeDuaApproval(teamMember),
                dataIdPrefix: 'deny-dua-approval'
            });
        }

        // The Request Admin's access to the data request cannot be denied (RQ-134) — hide on the admin row.
        if (
            teamMember.accessToDataRequest === ResearchTeamMemberAccessStatusEnum.Granted &&
            !teamMember.isAdmin &&
            teamMember.userId !== user.userId
        ) {
            resultMenuItems.push({
                title: 'Deny Access to data request',
                onClick: () => handleOnRevokeVmAccess(teamMember),
                dataIdPrefix: 'revoke-access'
            });
        }

        if (teamMember.accessToDataRequest !== ResearchTeamMemberAccessStatusEnum.Granted && teamMember.email !== user.email) {
            resultMenuItems.push({
                title: 'Grant Access to data request',
                onClick: () => handleOnGrantRequestAccessQuery(teamMember),
                dataIdPrefix: 'invite-member'
            });
        }

        if (canTransferAdminRole(teamMember)) {
            resultMenuItems.push({
                title: 'Transfer Administrator Role to Member',
                onClick: () => handleOnMakeOwner(teamMember),
                dataIdPrefix: 'transfer-administrator'
            });
        }

        if (isVivliAdmin && teamMember.userId?.length > 0) {
            if (teamMember.statusDto?.trainingCompleted) {
                resultMenuItems.push({
                    title: 'Set Data Access Training Not Completed',
                    onClick: () => onSetUnSetAccessTraining(false, teamMember),
                    dataIdPrefix: 'reset-access-training'
                });
            } else {
                resultMenuItems.push({
                    title: 'Set Data Access Training Completed',
                    onClick: () => onSetUnSetAccessTraining(true, teamMember),
                    dataIdPrefix: 'set-access-training'
                });
            }
        }

        return resultMenuItems;
    };

    const handleRenderResearcher = (teamMember: IResearchTeamMemberDetail) => {
        if (isActiveUserOrgAdmin() && !userCanManage) {
            return; //org admin cannot click on the row and see the popup
        }
        const newMember = createResearcher(ResearchTeamMemberRoleEnum.AdditionalResearcher);
        if (!draftMode) {
            const modalId = modalService.custom(
                <TeamMemberModalComponent
                    onSave={(teamMember) => {
                        modalService.dismiss(modalId);
                    }}
                    onCancel={() => modalService.dismiss(modalId)}
                    teamMember={teamMember}
                    dataRequest={dataRequest}
                    isAdminEditing={isAdminEditing}
                />,
                {
                    style: {
                        width: '80%',
                        height: '80%',
                        overflowY: 'auto'
                    }
                }
            );
        } else {
            let fieldIndex = members.findIndex(
                (member) =>
                    (teamMember.id && member.id === teamMember.id) ||
                    (teamMember.teamMemberId && member.teamMemberId === teamMember.teamMemberId)
            );
            if (fieldIndex < 0) {
                //this is for old or corrupted data requests where the id is missing - so we match on name as before
                fieldIndex = members.findIndex(
                    (member) => member.firstName === teamMember.firstName && member.lastName === teamMember.lastName
                );
            }
            const memberPath = getMemberArrayPath(fieldIndex);
            const modalId = modalService.custom(
                <DraftTeamMemberModalComponent
                    teamMember={teamMember}
                    onSave={(updatedTeamMember) => {
                        modalService.dismiss(modalId);
                        formApi.setValue(memberPath, updatedTeamMember, { shouldDirty: true });
                        formApi.trigger();
                    }}
                    onDelete={(updatedTeamMember) => {
                        modalService.dismiss(modalId);
                        handleOnRemove(teamMember);
                    }}
                    onCancel={() => {
                        modalService.dismiss(modalId);
                    }}
                    dataRequest={dataRequest}
                    userCanEdit={draftMode}
                    userCanDelete={userCanDelete && !!onRemove && !teamMember.isAdmin}
                />,
                {
                    style: {
                        width: '80%',
                        height: '80%',
                        overflowY: 'auto'
                    }
                }
            );
        }
    };

    // filter members to display in grid cards
    // check for validation errors in draft mode - only for non-lead researchers and non-statisticians
    // because the others can't be saved with validation errors.
    const filteredMembers = useMemo(() => {
        if (draftMode) {
            const checkedMembers = members;
            checkedMembers?.map((mem, index) => {
                const fields = formApi.getValues('researchTeam.researchTeamDetails');
                const memberFields = fields[index];
                const memberFieldKeys = Object.keys(memberFields || {});
                const hasAnyInvalidField = memberFieldKeys.some((key) => {
                    const fieldState = formApi.getFieldState(`researchTeam.researchTeamDetails.${index}.${key}`);
                    return fieldState?.invalid;
                });
                if (hasAnyInvalidField) {
                    mem.hasValidationError = true;
                }
            });

            return checkedMembers.filter((mem) => !mem.isLeadResearcher && !mem.isStatistician);
        }
        return members;
    }, [members]);

    // Returns a header component that stamps a stable data-testid on the header span.
    const makeHeader = (testId: string) =>
        ({ displayName }: IHeaderParams) => <span data-testid={testId}>{displayName}</span>;

    const columnDefs: ColDef[] = [
        {
            headerName: 'Name',
            filter: true,
            sortable: true,
            tooltipField: 'Name',
            headerTooltip: 'Name',
            minWidth: 150,
            headerComponent: makeHeader('research-team-col-name'),
            valueGetter: ({ data }) => {
                return `${data.firstName} ${data.lastName}`;
            },
            cellRenderer: GridCellRendererEnum.GridCellErrorIcon,
            cellRendererParams: ({ data }) => ({
                showErrorIcon: data.hasValidationError,
                dataTestId: `research-team-cell-name-${data.email}`
            })
        },
        {
            field: 'employerOrAffiliation',
            headerName: 'Affiliation',
            filter: true,
            sortable: true,
            headerTooltip: 'Affiliation',
            minWidth: 200,
            headerComponent: makeHeader('research-team-col-affiliation'),
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data }) => ({
                dataTestId: `research-team-cell-affiliation-${data.email}`
            })
        },
        {
            field: 'country.name',
            headerName: 'Country',
            headerTooltip: 'Country',
            filter: true,
            sortable: true,
            headerComponent: makeHeader('research-team-col-country'),
            cellRenderer: ({ value, data }) => (
                <span data-testid={`research-team-cell-country-${data.email}`}>{value}</span>
            )
        },
        {
            field: 'email',
            headerName: 'Email',
            headerTooltip: 'Email',
            filter: true,
            sortable: true,
            minWidth: 200,
            headerComponent: makeHeader('research-team-col-email'),
            cellRenderer: GridCellRendererEnum.GridCellLongText,
            cellRendererParams: ({ data }) => ({
                dataTestId: `research-team-cell-email-${data.email}`
            })
        },
        {
            field: 'rolesDto',
            headerName: 'Role(s)',
            headerTooltip: 'Role',
            sortable: false,
            minWidth: 210,
            cellStyle: { textAlign: 'left' },
            headerComponent: makeHeader('research-team-col-role'),
            cellRenderer: GridCellRendererEnum.GridCellList,
            cellRendererParams: ({ data }) => ({
                dataTestId: `research-team-cell-role-${data.email}`
            })
        },
        {
            headerName: 'Status Details',
            // DEV-516: computed on read from the structured statusDto (no longer a persisted statusArray).
            valueGetter: ({ data }) => buildStatusLabels((data as IResearchTeamMemberDetail)?.statusDto),
            filter: true,
            sortable: true,
            minWidth: 310,
            cellStyle: { textAlign: 'left' },
            headerComponent: makeHeader('research-team-col-status-details'),
            cellRenderer: GridCellRendererEnum.GridCellList,
            cellRendererParams: ({ data }) => ({
                dataTestId: `research-team-cell-status-details-${data.email}`
            })
        },
        {
            field: 'ellipsisMenu',
            headerName: '',
            sortable: true,
            maxWidth: 80,
            cellStyle: { textAlign: 'center' },
            headerComponent: makeHeader('research-team-col-menu'),
            // custom component renderer
            cellRenderer: GridCellRendererEnum.GridCustomDisplay,
            cellRendererParams: ({ data }) => ({
                component: (
                    <EllipsisDropdownMenuComponent
                        dataIdPrefix={data.lastName}
                        position={'top-end'}
                        items={getMenuItems(data)}
                    />
                )
            }),
            hide: !isVivliAdmin || isAdminEditing || draftMode
        }
    ];

    return (
        <GridView
            rowData={filteredMembers}
            columns={columnDefs}
            fullWidthColumns={false}
            hideFloatingFilter={true}
            autoHeight={true}
            onRowClick={handleRenderResearcher}
            ignoreClickForFields={['ellipsisMenu']}
            getRowTestId={(data: IResearchTeamMemberDetail) => `research-team-row-${data.email}`}
        />
    );
};
